import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search, X } from 'lucide-react';

/**
 * The pickers of the hiring windows (15 Oct 2026): one searchable select and one chip
 * multi-select, in the look of the approved mock-up. Each option is ONE line: a name (the
 * typed text marked in it), a muted line after it, small tags, and a short word on the
 * right ("56 free of 147", "Full", "Notice · 31 Oct"). An option that cannot be picked is
 * greyed, says why on hover, and ignores a click and Enter.
 *
 * The list is searched in the box at the top of the open panel. A picker can search its own
 * options (`filter="local"`), or hand the typed text to its owner (`filter="none"` with
 * `query`/`onQuery`), which reads the server and passes the answer back as `options`, with
 * a `footer` line saying how many there are.
 *
 * NOTHING IS CUT OFF (15 Oct 2026, the product owner): every option the picker holds is drawn,
 * in a list that scrolls. A list the server pages (people, stores) says it has more
 * (`hasMore`): scrolled to its end - or stepped past its last option with the arrow keys - it
 * asks its owner for the next page (`onReachEnd`), shows "Loading more…", and goes on until the
 * server says there is no more. Empty and failed states are ONE short line in the list; the
 * line under it is drawn only while there are options to count.
 *
 * The open panel is a FIXED portal to <body>, as SearchableSelect's: it is never clipped by a
 * scrolling window, follows the control on scroll and resize, flips above it when there is
 * no room below, and sits at z-index 140, above every Modal (see the ladder in
 * SearchableSelect). It closes on Escape and on a click outside. Closed by Escape or by a pick,
 * the keyboard is given back to the field (never left on the page's <body>); a click outside
 * leaves it where that click put it.
 */

export type PickerTone = 'blue' | 'amber' | 'grey';
export interface PickerTagView { text: string; tone: PickerTone; hover?: string }

export interface HiringSelectOption {
  value: string;
  label: string;
  /** Muted words after the name: "910482 · Brand Promoter · Tech Bazaar". */
  meta?: string | null;
  tags?: readonly PickerTagView[];
  /** The short words on the right: "56 free of 147", "Full", "Waiting". */
  right?: string | null;
  /** A tag on the right in place of words: "Notice · 31 Oct". */
  rightTag?: PickerTagView | null;
  /** Greyed: it cannot be picked. `hover` says why. */
  disabled?: boolean;
  hover?: string | null;
  /** A small heading drawn above the first option of each group. */
  group?: string | null;
}

const TONES: Record<PickerTone, string> = {
  blue: 'bg-primary-50 text-primary-700',
  amber: 'bg-amber-100 text-amber-800',
  grey: 'bg-gray-200 text-gray-600',
};

export function PickerTag({ tag }: { tag: PickerTagView }) {
  return (
    <span title={tag.hover} className={`inline-block whitespace-nowrap rounded-full px-2 py-px text-[11px] font-medium leading-4 ${TONES[tag.tone]}`}>
      {tag.text}
    </span>
  );
}

/** The typed text, marked wherever it is in `text` (any case). */
export function Marked({ text, typed }: { text: string; typed: string }) {
  const q = typed.trim();
  if (!q) return <>{text}</>;
  const lower = text.toLowerCase();
  const find = q.toLowerCase();
  const parts: ReactNode[] = [];
  let from = 0;
  let at = lower.indexOf(find, from);
  while (at >= 0) {
    if (at > from) parts.push(text.slice(from, at));
    parts.push(<mark key={at} className="rounded-sm bg-amber-200 px-px text-inherit">{text.slice(at, at + find.length)}</mark>);
    from = at + find.length;
    at = lower.indexOf(find, from);
  }
  if (from < text.length) parts.push(text.slice(from));
  return <>{parts}</>;
}

const matchesLocally = (option: HiringSelectOption, typed: string): boolean => {
  const q = typed.trim().toLowerCase();
  if (!q) return true;
  return option.label.toLowerCase().includes(q) || String(option.meta ?? '').toLowerCase().includes(q);
};

type PanelPos = { left: number; width: number; top?: number; bottom?: number };

/** The room kept between an open panel and each side of the window. */
export const PANEL_EDGE_PX = 8;

/**
 * The open panel's left and width: at least `minWidth` wide, its right edge on the anchor's -
 * but never wider than the window less 8px each side, and never past either side of it (a
 * phone's 375px has no room for the 520px people panel: it is drawn 359px wide, 8px in).
 */
export function panelBox(
  anchor: { left: number; right: number; width: number },
  minWidth: number,
  windowWidth: number,
): { left: number; width: number } {
  const room = Math.max(0, windowWidth - 2 * PANEL_EDGE_PX);
  const width = Math.min(Math.max(anchor.width, minWidth), room);
  const wanted = width > anchor.width ? anchor.right - width : anchor.left;
  const left = Math.max(PANEL_EDGE_PX, Math.min(wanted, windowWidth - PANEL_EDGE_PX - width));
  return { left, width };
}

/**
 * Where the open panel goes: under (or over) the anchor, at least `minWidth` wide, its right edge on the anchor's, within the window.
 * `watch` changes when the anchor may have changed size while open (a chip added): the panel is placed again.
 */
export function usePanelPosition(open: boolean, anchor: React.RefObject<HTMLElement | null>, minWidth: number, watch?: unknown): PanelPos | null {
  const [pos, setPos] = useState<PanelPos | null>(null);
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    const update = () => {
      const r = anchor.current?.getBoundingClientRect();
      if (!r) return;
      const H = 360;
      const below = window.innerHeight - r.bottom;
      const openUp = below < H && r.top > below;
      const box = panelBox(r, minWidth, document.documentElement.clientWidth || window.innerWidth);
      setPos({ ...box, ...(openUp ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, anchor, minWidth, watch]);
  return pos;
}

/** Closes on Escape and on a mouse-down outside the anchor and the panel, saying which. */
function useCloseOutside(open: boolean, inside: Array<React.RefObject<HTMLElement | null>>, close: (how: 'outside' | 'escape') => void) {
  const closeNow = useRef(close);
  closeNow.current = close;
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (inside.some((ref) => ref.current?.contains(t))) return;
      closeNow.current('outside');
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeNow.current('escape'); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
    // The refs are stable for the life of the picker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}

/** One option's line in the open list. */
function OptionLine({ option, typed, chosen, active, onPick, onHover }: {
  option: HiringSelectOption;
  typed: string;
  chosen: boolean;
  active: boolean;
  onPick: () => void;
  onHover: () => void;
}) {
  const off = option.disabled === true;
  // Named in words, as one line: the marked letters must not split a name in two.
  const name = [option.label, ...(option.tags ?? []).map((tag) => tag.text), option.meta, option.rightTag?.text ?? option.right]
    .filter(Boolean).join(' · ');
  return (
    <div
      role="option"
      aria-label={name}
      aria-selected={chosen}
      aria-disabled={off || undefined}
      title={option.hover ?? undefined}
      onMouseEnter={onHover}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => { if (!off) onPick(); }}
      className={`flex min-h-[32px] items-center justify-between gap-2.5 px-3 py-1 text-[13.5px] ${
        off ? 'cursor-not-allowed bg-gray-50 text-gray-400' : `cursor-pointer text-gray-700 ${active ? 'bg-gray-100' : ''}`
      }`}
    >
      <span className="flex min-w-0 items-baseline gap-2 overflow-hidden whitespace-nowrap">
        {/* A long name ends in "…" with the whole of it on hover; the muted words give way first. */}
        <span
          title={[option.label, option.hover].filter(Boolean).join(' · ')}
          className={`max-w-full flex-none truncate font-medium ${off ? 'text-gray-400' : 'text-gray-900'}`}
        >
          <Marked text={option.label} typed={typed} />
        </span>
        {(option.tags ?? []).map((tag) => <PickerTag key={tag.text} tag={tag} />)}
        {option.meta && (
          <span title={option.meta} className={`min-w-0 shrink-[1000] truncate text-[13px] ${off ? 'text-gray-400' : 'text-gray-500'}`}>{option.meta}</span>
        )}
      </span>
      <span className={`flex flex-none items-center gap-2 text-[12.5px] ${off ? 'text-gray-400' : 'text-gray-500'}`}>
        {option.rightTag ? <PickerTag tag={option.rightTag} /> : option.right}
        {chosen && <Check className="h-4 w-4 text-primary-600" aria-hidden="true" />}
      </span>
    </div>
  );
}

/** The open panel: the search box, the options (with their group headings), and the line under them. */
/** How close to the end of the list (px) a scroll asks for the next page. */
const NEAR_END_PX = 48;

function Panel({
  pos, panelRef, label, typed, onTyped, searchPlaceholder, options, value, onPick, emptyText, footer, loading, error, multi,
  hasMore = false, loadingMore = false, onReachEnd, onRetry, retryText = 'Try again',
}: {
  pos: PanelPos;
  panelRef: React.RefObject<HTMLDivElement | null>;
  label: string;
  typed: string;
  onTyped: (text: string) => void;
  searchPlaceholder: string;
  options: readonly HiringSelectOption[];
  value: (v: string) => boolean;
  onPick: (option: HiringSelectOption) => void;
  emptyText: string;
  footer?: string | null;
  loading?: boolean;
  error?: string | null;
  multi?: boolean;
  /** The server has more than the options drawn: the next page is asked at the end of the list. */
  hasMore?: boolean;
  loadingMore?: boolean;
  onReachEnd?: () => void;
  /** Where the list could not be read: "Try again" at the end of the `error` line reads it again. */
  onRetry?: () => void;
  retryText?: string;
}) {
  const [active, setActive] = useState(() => Math.max(0, options.findIndex((o) => !o.disabled)));
  const listRef = useRef<HTMLDivElement>(null);
  const reachEnd = useRef(onReachEnd);
  reachEnd.current = onReachEnd;
  const more = useRef({ hasMore, loadingMore });
  more.current = { hasMore, loadingMore };
  /** Asks for the next page, once per page: never while one is being read, nor past the last. */
  const askMore = () => {
    if (more.current.hasMore && !more.current.loadingMore) reachEnd.current?.();
  };
  // The options of a NEW search start at the top; a next page keeps where the person is.
  const firstValue = options[0]?.value;
  const firstEnabled = (): number => Math.max(0, options.findIndex((o) => !o.disabled));
  useEffect(() => {
    setActive(firstEnabled());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstValue]);
  useEffect(() => {
    setActive((was) => (was < options.length ? was : firstEnabled()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.length]);
  // A first page too short to scroll (a measured list that fits): the next one is asked at once.
  useEffect(() => {
    const el = listRef.current;
    if (el && el.scrollHeight > 0 && el.scrollHeight <= el.clientHeight) askMore();
  });
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${active}"]`) as HTMLElement | null;
    // jsdom has no scrollIntoView.
    if (typeof el?.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const step = (by: 1 | -1) => {
    if (options.length === 0) return;
    // At the last option, the arrow asks for the next page, as scrolling to the end does.
    if (by === 1 && active >= options.length - 1) {
      askMore();
      return;
    }
    let i = active;
    for (let n = 0; n < options.length; n += 1) {
      i = Math.min(options.length - 1, Math.max(0, i + by));
      if (!options[i].disabled) {
        setActive(i);
        if (by === 1 && i >= options.length - 1) askMore();
        return;
      }
    }
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); step(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); step(-1); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = options[active];
      if (hit && !hit.disabled) onPick(hit);
    }
  };

  let lastGroup: string | null | undefined;
  return createPortal(
    <div
      ref={panelRef}
      style={{ position: 'fixed', left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, zIndex: 140 }}
      className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl"
    >
      <div className="border-b border-gray-100 p-2">
        <div className="flex h-[30px] items-center gap-1.5 rounded-md border border-gray-200 px-2 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
          <Search className="h-3.5 w-3.5 flex-none text-gray-400" aria-hidden="true" />
          <input
            type="text"
            value={typed}
            onChange={(e) => onTyped(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            autoFocus
            className="w-full min-w-0 border-0 p-0 text-[13px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
          />
        </div>
      </div>
      <div
        ref={listRef}
        role="listbox"
        aria-label={label}
        aria-multiselectable={multi || undefined}
        aria-busy={loadingMore || undefined}
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.scrollTop + el.clientHeight >= el.scrollHeight - NEAR_END_PX) askMore();
        }}
        className="max-h-72 overflow-y-auto py-1"
      >
        {options.map((option, i) => {
          const heading = option.group && option.group !== lastGroup ? option.group : null;
          lastGroup = option.group;
          return (
            <div key={option.value} data-index={i}>
              {heading && <div role="presentation" className="px-3 pb-0.5 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{heading}</div>}
              <OptionLine
                option={option}
                typed={typed}
                chosen={value(option.value)}
                active={i === active}
                onPick={() => onPick(option)}
                onHover={() => { if (!option.disabled) setActive(i); }}
              />
            </div>
          );
        })}
        {/* One short line for an empty or failed list: never both, never beside the line below. */}
        {options.length === 0 && !loading && !error && <div className="px-3 py-2 text-[13px] text-gray-400">{emptyText}</div>}
        {error && (
          <div role="alert" className="px-3 py-2 text-[13px] text-red-700">
            {error}
            {onRetry && (
              <>
                {' '}
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={onRetry}
                  className="font-medium text-primary-600 hover:underline"
                >
                  {retryText}
                </button>
              </>
            )}
          </div>
        )}
        {loadingMore && <div role="status" className="px-3 py-2 text-[13px] text-gray-500">Loading more…</div>}
      </div>
      {(loading || (footer && options.length > 0)) && (
        <div role="status" className="border-t border-gray-100 px-3 py-1.5 text-xs text-gray-500">{loading ? 'Searching…' : footer}</div>
      )}
    </div>,
    document.body,
  );
}

export function HiringSelect({
  id, label, value, selected, onChange, options, placeholder, searchPlaceholder, emptyText = 'No matches', footer,
  filter = 'local', query, onQuery, onOpenChange, loading = false, error = null, clearable = false, clearLabel,
  disabled = false, invalid = false, title, minPanelWidth = 0, controlRight, hasMore = false, loadingMore = false, onReachEnd,
}: {
  id?: string;
  /** What a screen reader calls the control and its list: "Budget", "Who", "Round 1". */
  label: string;
  value: string;
  /** The chosen option, when the owner holds it (a search's options change as the person types). */
  selected?: HiringSelectOption | null;
  onChange: (value: string, option: HiringSelectOption | null) => void;
  options: readonly HiringSelectOption[];
  placeholder: string;
  searchPlaceholder: string;
  emptyText?: string;
  footer?: string | null;
  /** 'local': the typed text narrows `options` here. 'none': the owner searches (`onQuery`) and passes the answer back. */
  filter?: 'local' | 'none';
  query?: string;
  onQuery?: (typed: string) => void;
  /** Told each time the panel opens (true) and closes (false). */
  onOpenChange?: (open: boolean) => void;
  loading?: boolean;
  error?: string | null;
  /** The owner's list is one page of the server's: the next is asked (`onReachEnd`) at the end of the list. */
  hasMore?: boolean;
  loadingMore?: boolean;
  onReachEnd?: () => void;
  /** Draw a × that empties the field. */
  clearable?: boolean;
  clearLabel?: string;
  disabled?: boolean;
  /** Marked red: a refusal names what is chosen here. */
  invalid?: boolean;
  title?: string;
  /** The open panel is at least this wide (it then reaches left over the label column). */
  minPanelWidth?: number;
  /** Words on the right of the control, before the chevron ("56 free of 147"). */
  controlRight?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [ownTyped, setOwnTyped] = useState('');
  const typed = filter === 'none' ? query ?? '' : ownTyped;
  const setTyped = (text: string) => {
    if (filter === 'none') onQuery?.(text);
    else setOwnTyped(text);
  };
  const boxRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const comboRef = useRef<HTMLButtonElement>(null);
  const pos = usePanelPosition(open, boxRef, minPanelWidth);
  /** `giveFocusBack`: closed from the field or the list (Escape, a pick) - the field takes the keyboard back. */
  const close = (giveFocusBack = false) => {
    setOpen(false);
    onOpenChange?.(false);
    if (typed) setTyped('');
    if (giveFocusBack) comboRef.current?.focus();
  };
  useCloseOutside(open, [boxRef, panelRef], (how) => close(how === 'escape'));

  // Every option that matches: nothing is cut off.
  const shown = useMemo(
    () => (filter === 'local' ? options.filter((o) => matchesLocally(o, typed)) : [...options]),
    [options, typed, filter],
  );
  const chosen = selected !== undefined ? selected : options.find((o) => o.value === value) ?? null;
  const hasValue = value !== '' && !!chosen;

  return (
    <div ref={boxRef} className="relative min-w-0">
      <button
        ref={comboRef}
        id={id}
        type="button"
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-invalid={invalid || undefined}
        title={title ?? (chosen?.hover || undefined)}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          if (open) close(true);
          else {
            setOpen(true);
            onOpenChange?.(true);
          }
        }}
        className={`flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-lg border bg-white pl-3 pr-2.5 text-left text-sm focus:outline-none focus:ring-2 focus:ring-primary-100 ${invalid ? 'border-red-400 bg-red-50' : open ? 'border-primary-500 ring-2 ring-primary-100' : 'border-gray-300'} ${
          disabled ? 'cursor-not-allowed bg-gray-50 text-gray-400' : ''
        }`}
      >
        <span className="flex min-w-0 items-center gap-2 overflow-hidden whitespace-nowrap">
          {hasValue ? (
            <>
              {/* The name keeps its place; the tags beside it give way first (on a phone, two
                  tags would otherwise squeeze the name to nothing). They all show in the list. */}
              <span className="max-w-full flex-none truncate text-gray-900">{chosen!.label}{chosen!.meta ? <span className="text-gray-500">{` · ${chosen!.meta}`}</span> : null}</span>
              {(chosen!.tags ?? []).length > 0 && (
                <span className="flex min-w-0 shrink-[1000] items-center gap-2 overflow-hidden">
                  {(chosen!.tags ?? []).map((tag) => <PickerTag key={tag.text} tag={tag} />)}
                </span>
              )}
            </>
          ) : (
            <span className="truncate text-gray-400">{placeholder}</span>
          )}
        </span>
        <span className={`flex flex-none items-center gap-2 text-[13px] text-gray-500 ${clearable && hasValue && !disabled ? 'pl-6' : ''}`}>
          {hasValue && (controlRight ?? chosen!.right) && <span>{controlRight ?? chosen!.right}</span>}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
        </span>
      </button>
      {clearable && hasValue && !disabled && (
        <button
          type="button"
          aria-label={clearLabel ?? `Clear ${label.toLowerCase()}`}
          onClick={() => onChange('', null)}
          className="absolute right-8 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-600"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
      {open && pos && (
        <Panel
          pos={pos}
          panelRef={panelRef}
          label={label}
          typed={typed}
          onTyped={setTyped}
          searchPlaceholder={searchPlaceholder}
          options={shown}
          value={(v) => v === value && value !== ''}
          onPick={(option) => {
            onChange(option.value, option);
            close(true);
          }}
          emptyText={emptyText}
          footer={footer}
          loading={loading}
          error={error}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onReachEnd={onReachEnd}
        />
      )}
    </div>
  );
}

/**
 * Several people as chips: "Neha Kulkarni ×" for each one picked, then a grey placeholder -
 * "Choose recruiters" while nobody is picked, "Add…" after the chips - and a chevron, as the
 * other pickers' "Choose …". A click ANYWHERE in the field opens the searchable panel over the
 * people not yet picked, every one of them (nothing is cut off), the search box on top; a
 * click on a chip's × only removes that person. Until 15 Oct 2026 only the small "Add…" words
 * opened it, and a click on the rest of the field did nothing (the product owner: "not
 * showing any user list"). From the keyboard the field is one combobox: Enter, Space or
 * ArrowDown opens it, Escape closes it. The panel stays open, so several can be added in one
 * visit. A chip a refusal names is red and says why on hover.
 *
 * The open list is never an empty panel: `error` when it could not be read - with `onRetry`
 * as "Try again" in that line - `emptyText` when nobody matches what is typed (or the list
 * holds nobody), `allPickedText` when everyone listed is already picked. Closed by Escape or
 * a click on the field, the keyboard goes back to the field; closed by the last pick the
 * field can take (it is then full, with no field), to the last chip's ×.
 */
export function HiringChipsSelect({
  label, addLabel, values, options, onChange, removeLabel, marked = [], markedHover, limit, searchPlaceholder,
  emptyText = 'Nobody matches', allPickedText = 'Everyone listed is picked.', error = null, onRetry, retryText,
  disabled = false, placeholder = 'Choose…', addText = 'Add…',
}: {
  /** The group's name: "Recruiters". */
  label: string;
  /** What a screen reader calls the field: "Add a recruiter". */
  addLabel: string;
  values: readonly string[];
  options: readonly HiringSelectOption[];
  onChange: (values: string[]) => void;
  removeLabel: (option: HiringSelectOption) => string;
  marked?: readonly string[];
  markedHover?: string;
  limit?: number;
  searchPlaceholder: string;
  /** Nobody matches what is typed. */
  emptyText?: string;
  /** Nothing typed, and everyone listed is picked. */
  allPickedText?: string;
  /** The list could not be read: one red line in the open list, and the closed field's words in red. */
  error?: string | null;
  onRetry?: () => void;
  retryText?: string;
  disabled?: boolean;
  /** The closed field's words while nobody is picked: "Choose recruiters". */
  placeholder?: string;
  /** Its words after the chips. */
  addText?: string;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const comboRef = useRef<HTMLDivElement>(null);
  // A chip added while open may wrap the field onto another line: the panel is placed again.
  const pos = usePanelPosition(open, boxRef, 280, values.length);
  const focusBack = useRef(false);
  /** `giveFocusBack`: closed from the field or the list (Escape, a click on the field, the last pick). */
  const close = (giveFocusBack = false) => {
    setOpen(false);
    setTyped('');
    focusBack.current = giveFocusBack;
  };
  useCloseOutside(open, [boxRef, panelRef], (how) => close(how === 'escape'));
  // Once closed and drawn again: the field, or - when the last pick filled it and it is gone - the last chip's ×.
  useEffect(() => {
    if (open || !focusBack.current) return;
    focusBack.current = false;
    const removers = boxRef.current?.querySelectorAll<HTMLElement>('[data-chip-remove]');
    (comboRef.current ?? removers?.[removers.length - 1])?.focus();
  }, [open]);
  const groupId = useId();

  const picked = values.map((value) => options.find((o) => o.value === value) ?? { value, label: value });
  const left = useMemo(
    () => options.filter((o) => !values.includes(o.value) && matchesLocally(o, typed)),
    [options, values, typed],
  );
  const full = limit != null && values.length >= limit;
  const canOpen = !disabled && !full;
  const toggle = () => {
    if (open) close(true);
    else if (canOpen) setOpen(true);
  };
  const emptyLine = typed.trim() || options.length === 0 ? emptyText : allPickedText;
  const words = values.length === 0 ? placeholder : addText;

  return (
    <div
      ref={boxRef}
      role="group"
      aria-labelledby={groupId}
      onClick={(e) => {
        // A click in the open panel (a portal, which React still bubbles here) is the panel's own;
        // a chip's × removes its person and opens nothing.
        const target = e.target as Element;
        if (!boxRef.current?.contains(target) || target.closest('[data-chip-remove]')) return;
        toggle();
      }}
      className={`flex min-h-9 flex-wrap items-center gap-1.5 rounded-lg border bg-white py-1 pl-1.5 pr-2.5 ${
        canOpen ? 'cursor-pointer' : disabled ? 'cursor-not-allowed bg-gray-50' : ''
      } ${open ? 'border-primary-500 ring-2 ring-primary-100' : 'border-gray-300 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100'}`}
    >
      <span id={groupId} className="sr-only">{label}</span>
      {picked.map((option) => {
        const isMarked = marked.includes(option.value);
        return (
          <span
            key={option.value}
            title={isMarked ? markedHover : option.meta ?? undefined}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full py-0.5 pl-2.5 pr-1.5 text-[13px] ${
              isMarked ? 'bg-red-100 text-red-800' : 'bg-primary-50 text-primary-800'
            }`}
          >
            {option.label}
            <button
              type="button"
              data-chip-remove=""
              aria-label={removeLabel(option)}
              disabled={disabled}
              onClick={() => onChange(values.filter((v) => v !== option.value))}
              className="rounded-full text-primary-400 hover:text-primary-700 disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </span>
        );
      })}
      {!full && (
        <div
          ref={comboRef}
          role="combobox"
          tabIndex={disabled ? -1 : 0}
          aria-label={addLabel}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-disabled={disabled || undefined}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'ArrowDown') return;
            e.preventDefault();
            if (e.key === 'ArrowDown' && open) return;
            toggle();
          }}
          className={`flex h-7 min-w-[7rem] flex-1 items-center justify-between gap-2 overflow-hidden pl-1 text-sm focus:outline-none ${error ? 'text-red-700' : 'text-gray-400'}`}
        >
          <span className="truncate">{words}</span>
          <ChevronDown className={`h-4 w-4 flex-none text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
        </div>
      )}
      {open && pos && (
        <Panel
          pos={pos}
          panelRef={panelRef}
          label={label}
          typed={typed}
          onTyped={setTyped}
          searchPlaceholder={searchPlaceholder}
          options={error ? [] : left}
          value={() => false}
          onPick={(option) => {
            const next = [...values, option.value];
            onChange(next);
            setTyped('');
            if (limit != null && next.length >= limit) close(true);
          }}
          emptyText={emptyLine}
          error={error}
          onRetry={onRetry}
          retryText={retryText}
          multi
        />
      )}
    </div>
  );
}

export default HiringSelect;
